/**
 * Spatial-backend selection, kept free of `config.ts` / Vite so it can be unit
 * tested under plain tsx (`client/test/backendChain.test.ts`). `runtime.ts`
 * supplies the real factories and capability probes.
 */

import type { SpatialBackend } from '../spatial/SpatialBackend.js';

export type SpatialModeName = 'auto' | 'webxr' | 'alva' | 'vo' | 'orientation';

export interface BackendFactories {
  orientation: () => SpatialBackend;
  vo: () => SpatialBackend;
  alva: () => SpatialBackend;
  webxr: () => SpatialBackend;
}

export interface BackendCapabilities {
  webxr: () => Promise<boolean>;
  alva: () => Promise<boolean>;
}

/**
 * Backends to try, in order of preference.
 *
 * Ordered by what is PROVEN on hardware, not by what is best on paper.
 *
 * ⚠️ WebXR is DISCONNECTED from the live piece. `WebXRBackend.start()` awaits a
 * session that only `requestXRSession()` can create, and its only caller lived
 * in `scenes/Onboarding.tsx`, which the Rite replaced and nothing imports. A
 * WebXR backend therefore always reports 'unavailable'. It is unverified on
 * devices and takes over the screen, so re-enabling it is a product decision,
 * not a bug fix. Until then `VITE_SPATIAL_MODE=webxr` must not hand back a
 * stopped backend (which is what it did): it warns and takes the AlvaAR → VO
 * chain. 'auto' still lists WebXR first where advertised, because the chain
 * detects its failure and moves on; it simply never wins today.
 */
export async function planBackends(
  mode: SpatialModeName,
  caps: BackendCapabilities,
  make: BackendFactories,
  warn: (msg: string) => void = (m) => console.warn(m),
): Promise<(() => SpatialBackend)[]> {
  switch (mode) {
    case 'orientation':
      return [make.orientation];
    case 'vo':
      return [make.vo];
    case 'alva':
      return [make.alva];
    case 'webxr': {
      warn(
        '[spatial] VITE_SPATIAL_MODE=webxr: WebXR is not connected to the Rite (no code path ' +
          'requests an immersive-ar session), so it would start dead. Falling back to AlvaAR -> VO.',
      );
      const list: (() => SpatialBackend)[] = [];
      if (await caps.alva()) list.push(make.alva);
      list.push(make.vo);
      return list;
    }
    case 'auto':
    default: {
      const list: (() => SpatialBackend)[] = [];
      if (await caps.webxr()) list.push(make.webxr);
      if (await caps.alva()) list.push(make.alva);
      list.push(make.vo);
      return list;
    }
  }
}

/**
 * Start the best backend that ACTUALLY starts.
 *
 * Capability detection is not a promise of success: `isSessionSupported` answers
 * "could this hardware do AR", not "will a session open now". A backend is kept
 * only when it does not declare itself 'unavailable' AND `confirmStarted()`
 * produces evidence it is running (backends with nothing to prove are accepted).
 * See HISTORY 2026-08-17/18 for the Oppo and black-screen failures this encodes.
 *
 * `isCancelled` lets the runtime abandon a start that was superseded (restart,
 * StrictMode double-mount) without leaving a started backend behind: the
 * backend in flight is stopped and returned, and the caller discards it.
 */
export async function startSpatialBackend(
  candidates: (() => SpatialBackend)[],
  attach: (b: SpatialBackend) => void,
  fallback: () => SpatialBackend,
  isCancelled: () => boolean = () => false,
): Promise<SpatialBackend> {
  let last: SpatialBackend | null = null;

  for (const make of candidates) {
    if (isCancelled()) break;
    const backend = make();
    try {
      attach(backend);
      await backend.start();
      if (isCancelled()) {
        safeStop(backend);
        return backend;
      }

      // Two separate questions, and conflating them is what shipped a black
      // screen: did setup fail, and is it actually working?
      const declaredDead = backend.trackingState?.() === 'unavailable';
      // A backend with no confirmStarted has nothing to prove — accept it.
      const presenting = declaredDead ? false : ((await backend.confirmStarted?.(3000)) ?? true);

      if (isCancelled()) {
        safeStop(backend);
        return backend;
      }
      if (presenting) return backend;

      // Release whatever it holds (an XR session, a WASM instance) before the
      // next candidate tries to claim the same camera.
      backend.stop();
    } catch {
      safeStop(backend);
    }
    last = backend;
  }

  // Everything refused. Return the last so the app still has an object to talk
  // to; the HUD reports the failure rather than the field crashing.
  return last ?? fallback();
}

function safeStop(b: SpatialBackend): void {
  try {
    b.stop();
  } catch {
    /* already gone */
  }
}
